import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { SuretyAccountId } from "@inrev/common";
import { useFetchLatestSuretyAccountUnderwriting } from "@portal/api/account";
import { cn } from "@portal/components/lib/utils";
import { Card, Icon, Text } from "@portal/components/primitives";
import { FC, useMemo } from "react";

type AccountReviewReasonsBannerProps = {
	accountId: SuretyAccountId;
};

const UNDERWRITER_REVIEW_COPY =
	"An underwriter is taking a closer look at this request and may reach out with additional questions";

export const AccountReviewReasonsBanner: FC<AccountReviewReasonsBannerProps> = ({ accountId }) => {
	const { latestUnderwriting, latestUnderwritingIsLoading, latestUnderwritingError } =
		useFetchLatestSuretyAccountUnderwriting(accountId);

	const reviewReasonDescriptions = useMemo(() => {
		const reviewReasons = latestUnderwriting?.reviewReasons;
		if (!reviewReasons || reviewReasons.length === 0) {
			return [];
		}
		return Array.from(new Set(reviewReasons.map((reason) => reason.description)));
	}, [latestUnderwriting?.reviewReasons]);

	if (latestUnderwritingIsLoading || latestUnderwritingError) {
		return null;
	}

	if (!latestUnderwriting || reviewReasonDescriptions.length === 0) {
		return null;
	}

	return (
		<Card elevation="raised" className="rounded-xl" data-testid="agent-account-review-reasons-card">
			<Disclosure defaultOpen>
				{({ open }) => (
					<Card.Body className="p-6">
						<DisclosureButton
							data-testid="agent-account-review-reasons-toggle"
							className="flex w-full items-center gap-2 text-left cursor-pointer"
						>
							<Text type="title" color="primary" className="flex-1">
								Underwriter Review
							</Text>
							<Icon
								type="chevron-right"
								size="mini"
								color="tertiary"
								className={cn("transition-transform duration-150", open && "rotate-90")}
							/>
						</DisclosureButton>
						<div className="mt-5 flex items-center gap-2">
							<Icon type="headset" color="tertiary" size="mini" />
							<Text type="body" color="secondary">
								{UNDERWRITER_REVIEW_COPY}
							</Text>
						</div>
						<DisclosurePanel>
							<ol className="mt-5 flex list-decimal list-inside flex-col space-y-2">
								{reviewReasonDescriptions.map((description) => (
									<li key={description} className="whitespace-pre-wrap">
										<Text type="body" color="primary">
											{description}
										</Text>
									</li>
								))}
							</ol>
						</DisclosurePanel>
					</Card.Body>
				)}
			</Disclosure>
		</Card>
	);
};
